/**
 * Typed wrappers around culori for use in Sutra.
 * Types are declared in src/types/culori.d.ts following the project's
 * ambient-module pattern for third-party libraries without bundled .d.ts files.
 */

import { clampChroma, formatHex, oklch, parse, rgb } from 'culori'

/** Parse any CSS color string (oklch, hex, rgb, hsl, …) to internal culori form. */
export function parseColor(color: string): ReturnType<typeof parse> {
  return parse(color)
}

/** Convert any parsed color to its OKLCH representation. */
export function toOklch(
  color: ReturnType<typeof parse>,
): { l: number; c: number; h: number } | undefined {
  return oklch(color)
}

/** Convert any parsed color to sRGB {r,g,b} with values in 0–1. */
export function toRgb(
  color: ReturnType<typeof parse>,
): { r: number; g: number; b: number } | undefined {
  return rgb(color)
}

/** Clamp chroma so the color fits inside the sRGB gamut. */
export function clampToSrgb(color: {
  mode: string
  l: number
  c: number
  h: number
}): { l: number; c: number; h: number } {
  return clampChroma(color, 'oklch')
}

/**
 * Return the OKLCH lightness (0–1) of a CSS color string.
 * Returns 0.5 for unparseable input.
 */
export function oklchLightness(colorStr: string): number {
  const parsed = parseColor(colorStr)
  if (!parsed) return 0.5
  return toOklch(parsed)?.l ?? 0.5
}

/**
 * Format any CSS color string as a #rrggbb hex string.
 * Returns '#000000' for unparseable input.
 */
export function colorToHex(color: string): string {
  const parsed = parseColor(color)
  if (!parsed) return '#000000'
  return formatHex(parsed) ?? '#000000'
}

/**
 * Parse a CSS color string to [r, g, b] in the 0–255 range.
 * Returns [0, 0, 0] (black) for unparseable input.
 */
export function colorToRgb255(color: string): [number, number, number] {
  const parsed = parseColor(color)
  if (!parsed) return [0, 0, 0]
  const { r = 0, g = 0, b = 0 } = toRgb(parsed) ?? {}
  return [
    Math.round(Math.max(0, Math.min(1, r)) * 255),
    Math.round(Math.max(0, Math.min(1, g)) * 255),
    Math.round(Math.max(0, Math.min(1, b)) * 255),
  ]
}
